/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import { Container, Grid, Typography } from "@material-ui/core";
import {
	DataGridPro,
	GridColumns,
	GridToolbarColumnsButton,
	GridToolbarDensitySelector,
	GridToolbarExport,
	GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import PageBreadcrumbs from "../components/Breadcrumbs/PageBreadcrumbs";
import { breadcrumbNode } from "../components/Breadcrumbs/PageBreadcrumbs.d";
import { useBatchUpdateInventory } from "../api/replenishmentApi";
import { Box, Button, Tooltip, Snackbar, Alert } from "@mui/material";
import { DownloadOutlined, FileUploadOutlined } from "@mui/icons-material";

interface DisplayRequiredRow {
	id: string;
	sku: string;
	locationId: number;
	displayRequired: boolean;
}

const DisplayRequired: React.FC = () => {
	const breadcrumbNodes: breadcrumbNode[] = [{ name: "Update Display Required" }];

	const [rows, setRows] = useState<DisplayRequiredRow[]>([]);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" } | null>(
		null
	);
	const [{ loading: isSaving }, batchUpdateInventory] = useBatchUpdateInventory();

	const columns: GridColumns = [
		{ field: "sku", headerName: "SKU", width: 150, editable: false },
		{ field: "locationId", headerName: "Location ID", type: "number", width: 130, editable: false },
		{ field: "displayRequired", headerName: "Display Required", type: "boolean", width: 150, editable: true },
	];

	const handleDownloadTemplate = useCallback(() => {
		const csvContent = "Sku,LocationId,DisplayRequired\n";
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", "display_required_template.csv");
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, []);

	const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const text = e.target?.result as string;
				const lines = text.split("\n");
				const headers = lines[0]
					.toLowerCase()
					.split(",")
					.map((h) => h.trim());

				// Validate headers
				const expectedHeaders = ["sku", "locationid", "displayrequired"];
				const isValidHeader = expectedHeaders.every((header) => headers.includes(header));

				if (!isValidHeader) {
					setSnackbar({
						open: true,
						message: "Invalid CSV format. Expected headers: Sku,LocationId,DisplayRequired",
						severity: "error",
					});
					return;
				}

				const newRows: DisplayRequiredRow[] = [];
				for (let i = 1; i < lines.length; i++) {
					const line = lines[i].trim();
					if (line) {
						const values = line.split(",").map((v) => v.trim());
						if (values.length >= 3) {
							const displayRequiredValue = values[2]?.toLowerCase();
							const skuValue = values[0]
							const locationIdValue = values[1]
							
							// Validate boolean value
							if (displayRequiredValue !== 'true' && displayRequiredValue !== 'false' ) {
								setSnackbar({
									open: true,
									message: `Invalid DisplayRequired value at row ${i + 1}. Must be 'true' or 'false'.`,
									severity: "error",
								});
								return;
							}
							// Validate locationId value
							if (!parseInt(locationIdValue)) {
								setSnackbar({
									open: true,
									message: `Invalid LocationId value at row ${i + 1}. Must be a number.`,
									severity: "error",
								});
								return;
							}

							// Validate SKU value
							if (!parseInt(skuValue)) {
								setSnackbar({
									open: true,
									message: `Invalid SKU value at row ${i + 1}. Must be a number.`,
									severity: "error",
								});
								return;
							}
							
							const row: DisplayRequiredRow = {
								id: `${values[0]}-${values[1]}`,
								sku: values[0],
								locationId: parseInt(values[1]),
								displayRequired: displayRequiredValue === 'true',
							};
							newRows.push(row);
						}
					}
				}
				setRows(newRows);
			};
			reader.readAsText(file);
		}
		// Reset the input value so the same file can be uploaded again
		event.target.value = "";
	}, []);

	const handleSave = useCallback(async () => {
		const updateRequests = rows.map((row) => ({
			sku: row.sku,
			locationId: row.locationId,
			displayRequired: row.displayRequired,
		}));

		await batchUpdateInventory(updateRequests)
			.then(() => {
				setSnackbar({
					open: true,
					message: "Display required updates saved successfully!",
					severity: "success",
				});
			})
			.catch((error) => {
				console.log(`${error.message} | ${error?.response?.data}`);
				setSnackbar({
					open: true,
					message: error?.message
						? `Error saving display required updates: ${error.message}`
						: "Error saving display required updates. Please try again.",
					severity: "error",
				});
			});
	}, [rows, batchUpdateInventory]);

	const handleRowEdit = useCallback((updatedRow: DisplayRequiredRow) => {
		setRows((prevRows) => prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
		return updatedRow;
	}, []);

	const CustomToolbar = () => {
		return (
			<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<GridToolbarColumnsButton />
					<GridToolbarFilterButton />
					<GridToolbarDensitySelector />
					<GridToolbarExport />
					<Tooltip title="Download Import Template">
						<Button size="small" startIcon={<DownloadOutlined />} onClick={handleDownloadTemplate} sx={{ height: 36 }}>
							Template
						</Button>
					</Tooltip>
					<input
						id="display-required-import-input"
						type="file"
						accept=".csv"
						style={{ display: "none" }}
						onChange={handleImport}
					/>
					<label htmlFor="display-required-import-input">
						<Button size="small" startIcon={<FileUploadOutlined />} component="span" sx={{ height: 36 }}>
							Import
						</Button>
					</label>
					{rows.length > 0 && (
						<Button color="primary" onClick={() => handleSave()} disabled={isSaving} size="small" sx={{ height: 36 }}>
							{isSaving ? "Saving..." : "Save All"}
						</Button>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={6}>
				<Grid container item direction="row">
					<Grid item xs={12} sm={8}>
						<PageBreadcrumbs nodes={breadcrumbNodes} />
					</Grid>
				</Grid>
				<Grid container>
					<Typography variant="h4">Display Required Updates</Typography>
				</Grid>
				<Grid container>
					<Typography>Upload a CSV file to set display required flags or download the template to get started.</Typography>
				</Grid>
				<Grid item xs={12}>
					<DataGridPro
						autoHeight
						density="standard"
						rows={rows}
						columns={columns}
						paginationMode="client"
						rowsPerPageOptions={[25, 50, 100]}
						pagination
						processRowUpdate={handleRowEdit}
						onProcessRowUpdateError={(error) => console.error(error)}
						components={{
							Toolbar: CustomToolbar,
						}}
						initialState={{
							pagination: {
								pageSize: 25,
							},
							sorting: {
								sortModel: [{ field: "sku", sort: "asc" }],
							},
						}}
					/>
				</Grid>
			</Grid>
			<Snackbar
				open={!!snackbar?.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
					severity={snackbar?.severity}
					sx={{
						width: "100%",
						bgcolor: snackbar?.severity === "success" ? "success.main" : "error.main",
						color: "#fff",
					}}
				>
					{snackbar?.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default DisplayRequired;
